import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  TerminationProvider,
  useTermination,
} from "@/contexts/TerminationContext";
import DashboardContainer from "@/components/layout/DashboardContainer";
import CombinedDetailsStep from "./steps/CombinedDetailsStep";
import FinalPayStep from "./steps/FinalPayStep";
import SummaryStep from "./steps/SummaryStep";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import { EmployeeOverviewResponse } from "@/components/EmployeeOverview";
import { EmployeeDemographicInfo } from "@/components/types/employeeTypes";
import { useForm } from "react-hook-form";
import { Form } from "@/components/ui/form";

// Wizard steps
const STEPS = [
  { id: "details", title: "Termination Details & Notice" },
  { id: "pay", title: "Final Pay Calculation" },
  { id: "summary", title: "Summary & Submission" },
];

const TerminationWizard: React.FC = () => {
  const { employeeId } = useParams<{ employeeId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [employee, setEmployee] = useState<EmployeeDemographicInfo | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { currentTermination, getTermination, error } = useTermination();

  // Initialize the form
  const form = useForm({
    defaultValues: {
      terminationReason: "",
      terminationDate: "",
      requiredNoticeDays: 0,
      payThroughDate: "",
    },
  });

  // Fetch employee details and termination data
  useEffect(() => {
    if (!employeeId) return;

    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Fetch employee details
        const employeeData = await ApiService.request<EmployeeOverviewResponse>(
          EndpointKeys.GET_EMPLOYEE_DETAILS,
          {
            urlParams: { id: employeeId },
          },
        );
        setEmployee(employeeData.demographicInfo);

        // Fetch termination data
        await getTermination(employeeId);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast({
          title: "Error",
          description: "Failed to fetch employee or termination data",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [employeeId, getTermination, toast]);

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCancel = () => {
    navigate(`/employee/${employeeId}`);
  };

  // Determine if the next button should be disabled
  const isNextDisabled = () => {
    // This is a placeholder for validation logic
    // It should be replaced with actual validation based on the current step
    if (!currentTermination) return true;

    switch (currentStep) {
      case 0: // Combined Termination Details & Notice Period
        const formPayThroughDate = form.getValues().payThroughDate;
        return (
          !currentTermination.termination_reason ||
          !currentTermination.termination_date ||
          (!currentTermination.noticeCalculation?.payThroughDate &&
            !formPayThroughDate)
        );
      case 1: // Final Pay
        console.log("Checking pay components:", {
          hasComponents: !!currentTermination.final_pay_components,
          length: currentTermination.final_pay_components?.length,
          components: currentTermination.final_pay_components,
        });

        // If we see components on screen but they're not in currentTermination,
        // let's allow the Next button even if the array is empty (bug fix)
        if (document.querySelectorAll("table tbody tr").length > 0) {
          return false;
        }

        return (
          !currentTermination.final_pay_components ||
          currentTermination.final_pay_components.length === 0
        );
      default:
        return false;
    }
  };

  // Render appropriate step component
  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <CombinedDetailsStep
            employeeId={employeeId}
            employeeStartDate={employee?.actualEffectiveDate}
            form={form}
          />
        );
      case 1:
        return <FinalPayStep form={form} />;
      case 2:
        return <SummaryStep employeeDetails={employee} form={form} />;
      default:
        return null;
    }
  };

  // Render loading state if data is being fetched
  if (isLoading) {
    return (
      <DashboardContainer>
        <div className="flex h-full w-full items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardContainer>
    );
  }

  // Render error message if there was an error fetching data
  if (error) {
    return (
      <DashboardContainer>
        <div className="flex h-full w-full flex-col items-center justify-center gap-4">
          <p className="text-red-500">{error}</p>
          <Button onClick={handleCancel}>Return to Employee</Button>
        </div>
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer>
      <div className="mx-auto max-w-4xl w-full">
        {/* Step navigation */}
        <div className="mb-8">
          <ul className="flex border-b border-gray-200">
            {STEPS.map((step, index) => (
              <li
                key={step.id}
                className={`mr-1 ${index <= currentStep ? "border-b-2 border-primary font-medium text-primary" : "text-gray-500"}`}
              >
                <div className="py-4 px-4 cursor-default">{step.title}</div>
              </li>
            ))}
          </ul>
        </div>

        {/* Step content */}
        <div className="mb-8">
          <Form {...form}>
            <form>{renderStep()}</form>
          </Form>
        </div>

        {/* Navigation buttons */}
        <div className="flex justify-between">
          <div>
            {currentStep > 0 && (
              <Button variant="outline" onClick={handlePrevious}>
                Previous
              </Button>
            )}
            <Button variant="outline" onClick={handleCancel} className="ml-2">
              Cancel
            </Button>
          </div>
          {currentStep < STEPS.length - 1 && (
            <Button onClick={handleNext} disabled={isNextDisabled()}>
              Next
            </Button>
          )}
        </div>
      </div>
    </DashboardContainer>
  );
};

// Wrap the wizard with the TerminationProvider
const TerminationContainer: React.FC = () => {
  return (
    <TerminationProvider>
      <TerminationWizard />
    </TerminationProvider>
  );
};

export default TerminationContainer;
